// frontend/src/pages/Proposals/ProposalNew.jsx
// Vendor-only: submit a new agreement proposal to Rhaya Legal
import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ChevronLeft, Upload, X } from "lucide-react";
import { submitProposal } from "../../api/proposal.api.js";
import { ErrorMessage } from "../../components/ui";

export default function ProposalNew() {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [form, setForm] = useState({ title: "", purpose: "", description: "" });
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("purpose", form.purpose.trim());
      if (form.description.trim()) fd.append("description", form.description.trim());
      if (file) fd.append("ownTemplate", file);
      return submitProposal(fd);
    },
    onSuccess: () => navigate("/portal/proposals"),
    onError: (e) => setError(e.response?.data?.error || "Gagal mengirim pengajuan"),
  });

  return (
    <div className="max-w-2xl mx-auto">
      <Link
        to="/portal/proposals"
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={15} /> Kembali ke Pengajuan
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pengajuan Perjanjian Baru</h1>
        <p className="mt-1 text-sm text-gray-500">
          Ajukan proposal perjanjian kerja sama kepada tim Legal Rhaya Group.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          submit.mutate();
        }}
        className="card p-6 space-y-5"
      >
        <div>
          <label className="label">
            Judul Perjanjian <span className="text-red-500">*</span>
          </label>
          <input
            className="input"
            placeholder="contoh: Perjanjian Kerja Sama Pengadaan Barang"
            value={form.title}
            onChange={set("title")}
            required
          />
        </div>

        <div>
          <label className="label">
            Maksud & Tujuan <span className="text-red-500">*</span>
          </label>
          <textarea
            className="input"
            rows={4}
            placeholder="Jelaskan tujuan perjanjian ini secara singkat..."
            value={form.purpose}
            onChange={set("purpose")}
            required
          />
        </div>

        <div>
          <label className="label">Keterangan Tambahan</label>
          <p className="text-xs text-gray-400 mb-1.5">Opsional — informasi lain untuk tim Legal.</p>
          <textarea
            className="input"
            rows={3}
            placeholder="Informasi lain yang perlu disampaikan ke tim Legal..."
            value={form.description}
            onChange={set("description")}
          />
        </div>

        <div>
          <label className="label">Template Perjanjian</label>
          <p className="text-xs text-gray-400 mb-1.5">
            Opsional — jika perusahaan Anda memiliki format perjanjian sendiri (DOCX, maks. 30MB).
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
          {file ? (
            <div className="flex items-center gap-2 p-3 border border-brand-200 bg-brand-50 rounded-lg">
              <Upload size={14} className="text-brand-500 shrink-0" />
              <span className="text-sm text-brand-700 font-medium flex-1 truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
                className="text-gray-400 hover:text-red-500"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-4 py-3 w-full border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 transition-colors"
            >
              <Upload size={14} />
              Pilih file DOCX
            </button>
          )}
        </div>

        {error && <ErrorMessage error={error} />}

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/portal/proposals" className="btn-secondary">
            Batal
          </Link>
          <button type="submit" disabled={submit.isPending} className="btn-primary">
            {submit.isPending ? "Mengirim..." : "Kirim Pengajuan"}
          </button>
        </div>
      </form>
    </div>
  );
}
